<!-- AccountSummary.vue -->
<script setup>
// ✅ MUDANÇA: importa watch para reagir ao reloadSignal vindo do pai.
import { ref, onMounted, computed, onUnmounted, watch } from "vue";
import { useUserStore } from "@/stores/useUserStore";
import { useSubscriptionStore } from "@/stores/useSubscriptionStore";
import { useManagedAccountStore } from "@/stores/useManagedAccountStore";
import { format, addDays } from "date-fns";
import { formatDateTimeForDisplay } from "@/utils/dateUtils";
import DeactivationConfirmModal from "@/components/User/DeactivationConfirmModal.vue";
import CancelImmediateConfirmModal from "@/components/Profile/CancelImmediateConfirmModal.vue";
import PauseSubscriptionConfirmModal from "@/components/Profile/PauseSubscriptionConfirmModal.vue";
import ReactivateSubscriptionConfirmModal from "@/components/Profile/ReactivateSubscriptionConfirmModal.vue";
import RefundConfirmModal from "@/components/Profile/RefundConfirmModal.vue";
import RefundResultModal from "@/components/Profile/RefundResultModal.vue";
import api from "@/services/api";

// ✅ NOVO: prop reloadSignal.
//
// Contexto do bug que isso resolve:
// O AccountSummary é montado dentro do AccountSettingsModal e permanece
// montado enquanto o UpgradeModal (catálogo de planos) abre por cima.
// Quando o usuário paga um novo plano e o UpgradeModal fecha, o AccountSummary
// volta a ficar visível — mas como ele já estava montado, onMounted() NÃO
// dispara de novo, então currentUser.planName, cotas etc. ficam stale.
//
// O pai (AccountSettingsModal → ancestral que orquestra os modais) incrementa
// reloadSignal sempre que o UpgradeModal fecha. O watcher abaixo detecta isso
// e dispara fetchData(true), recarregando usuário, assinaturas e cotas.
const props = defineProps({
  reloadSignal: {
    type: Number,
    default: 0,
  },
});

/**
 * Emite:
 *  - 'openUpgradeModal'    → para abrir o modal no DashboardLayout
 *  - 'subscriptionChanged' → ✅ NOVO: sinaliza ao pai que a assinatura mudou
 *
 * Contexto do bug que 'subscriptionChanged' resolve:
 * Após cancelar/pausar/reativar/estornar uma assinatura, o AccountSummary
 * recarrega a si mesmo via fetchData(true). Mas o DashboardLayout mantém suas
 * PRÓPRIAS cópias de currentUser e subscriptions (via stores) e seu próprio
 * currentSubscriptionInfo — e nada o avisa para recalcular. Resultado: o badge
 * do plano no header continua mostrando o plano antigo.
 *
 * Emitindo 'subscriptionChanged' a cada ação que altera a assinatura, o pai
 * (AccountSettingsModal → DashboardLayout) pode reexecutar o refresh das stores
 * e recalcular o badge. O DashboardLayout reaproveita refreshStoresAfterDowngrade()
 * para isso.
 */
const emit = defineEmits(["openUpgradeModal", "subscriptionChanged"]);

const userStore = useUserStore();
const subscriptionStore = useSubscriptionStore();
const managedAccountStore = useManagedAccountStore();

// Constante para conversão de caracteres para páginas
const CHARS_PER_PAGE = 2450;

// ✅ Controle do Modal de Desativação
const showDeactivateMenu = ref(false);
const isDeactivating = ref(false);

// ====================================================================
// ✅ LÓGICA DE TOAST (ESTILO PROFILEFORM) 🚀
// ====================================================================
const showToast = ref(false);
const toastMessage = ref("");
const toastTimeoutId = ref(null);
const toastType = ref("error"); // "error" | "success"

const isLoading = ref(true);

// ═══════════════════════════════════════════════════════════════
// Cancelamento imediato (3 variantes: active-paid, paused, pending)
// Todas disparam o mesmo modal CancelImmediateConfirmModal,
// que se adapta via prop `variant`.
// ═══════════════════════════════════════════════════════════════
const showCancelConfirm = ref(false);
const isCancelling = ref(false);
const cancelVariant = ref("active-paid"); // active-paid | paused | pending

function openCancelConfirm() {
  // ✅ NOVO: se qualquer ação já estiver em andamento,
  // não permite abrir outro fluxo de ação.
  if (isAnyAccountActionRunning.value) return;

  // Determina a variante a partir do estado atual da assinatura.
  // A ordem dos checks importa: paused tem precedência sobre os outros,
  // porque uma sub pausada pode ter PaymentStatus.Pending (caso raro,
  // mas conceitualmente possível durante reativações em andamento).
  if (isPaused.value) {
    cancelVariant.value = "paused";
  } else if (!canPauseAtPeriodEnd.value) {
    cancelVariant.value = "pending";
  } else {
    cancelVariant.value = "active-paid";
  }

  showCancelConfirm.value = true;
}

async function handleConfirmCancel() {
  // ✅ ALTERADO: antes bloqueava apenas cancelamento repetido.
  // Agora bloqueia se qualquer ação do componente estiver em andamento.
  if (isAnyAccountActionRunning.value) return;

  isCancelling.value = true;
  showCancelConfirm.value = false;

  // Reason específica por variante — útil para auditoria nos logs do backend.
  const reasonByVariant = {
    "active-paid": "Cancelamento imediato solicitado pelo usuário",
    paused: "Cancelamento imediato a partir de assinatura pausada",
    pending: "Cancelamento imediato antes da confirmação do pagamento",
  };

  try {
    // ✅ MUDANÇA: captura o DTO retornado. O backend agora responde HTTP 200
    // tanto no cancelamento normal quanto na reconciliação (assinatura já
    // cancelada direto no painel do MP) — antes a reconciliação lançava 400,
    // o frontend caía no catch e abortava fetchData/emit, deixando o badge
    // de plano stale até um 2º clique.
    const result = await subscriptionStore.cancelActiveSubscription({
      cancelAtPeriodEnd: false,
      reason: reasonByVariant[cancelVariant.value],
    });
    await fetchData(true);

    // ✅ NOVO: avisa o pai (→ DashboardLayout) que a assinatura mudou,
    // para que o badge de plano do header seja recalculado.
    emit("subscriptionChanged");

    // ✅ MUDANÇA (Opção B): usa sempre a mensagem retornada pelo backend.
    // No cancelamento, tanto o caminho normal quanto o reconciliado encerram
    // a assinatura (Status="Cancelled"); o backend é a fonte única do texto
    // do toast. Fallback fixo só para o caso de Message vir vazio.
    await showSuccessToast(
      result?.message ||
        result?.Message ||
        "Assinatura cancelada e plano Free atribuído."
    );
  } catch (err) {
    showErrorToast(
      err?.message || "Não foi possível cancelar a assinatura. Tente novamente."
    );
  } finally {
    isCancelling.value = false;
  }
}
// ═══════════════════════════════════════════════════════════════
// Pausa de renovação (cancelAtPeriodEnd = true)
// Disparado pelo botão "Pausar renovação", visível apenas quando
// a sub está ativa com PaymentStatus.Approved.
// ═══════════════════════════════════════════════════════════════
const showPauseConfirm = ref(false);
const isPausing = ref(false);

async function handleConfirmPause() {
  // ✅ ALTERADO: bloqueia se qualquer ação do componente estiver em andamento.
  if (isAnyAccountActionRunning.value) return;

  isPausing.value = true;
  showPauseConfirm.value = false;

  try {
    // ✅ MUDANÇA: captura o DTO retornado. O backend agora responde HTTP 200
    // tanto na pausa normal quanto na reconciliação (assinatura já cancelada
    // direto no painel do MP). Os dois casos se distinguem pelo campo Status:
    //   - pausa normal       → Status === "Active"  (sub segue ativa, pausada)
    //   - reconciliação→Free → Status === "Cancelled"
    const result = await subscriptionStore.cancelActiveSubscription({
      cancelAtPeriodEnd: true,
      reason: "Parar renovação ao fim do período",
    });
    await fetchData(true);

    // ✅ NOVO: a pausa não troca o plano imediatamente, mas altera o estado
    // da assinatura (cancelAtPeriodEnd). Avisa o pai mesmo assim para manter
    // as stores do DashboardLayout consistentes.
    emit("subscriptionChanged");

    // ╔══════════════════════════════════════════════════════════════════╗
    // ║ ✅ NOVO (reconciliação de pausa sobre assinatura já cancelada):   ║
    // ║                                                                  ║
    // ║ Quando a assinatura já foi cancelada direto no painel do MP, o    ║
    // ║ backend não consegue pausá-la: ele reconcilia o estado para       ║
    // ║ Cancelled + Free + crédito proporcional e retorna o DTO com       ║
    // ║ Status="Cancelled" (HTTP 200). Esse é um caso de SUCESSO — o      ║
    // ║ objetivo do usuário (encerrar a renovação) foi atingido, ainda    ║
    // ║ que por um caminho diferente do pedido.                           ║
    // ║                                                                  ║
    // ║ Detecção pela abordagem A: numa pausa NORMAL o backend retorna    ║
    // ║ Status="Active"; só a reconciliação retorna "Cancelled". Logo,    ║
    // ║ Status==="Cancelled" aqui ⇒ houve reconciliação.                  ║
    // ║                                                                  ║
    // ║ Comportamento:                                                   ║
    // ║   - pausa normal  → nenhum toast (o modal já comunicou a ação,    ║
    // ║                     o estado pausado fica visível no card).      ║
    // ║   - reconciliação → toast de SUCESSO com a mensagem do backend.   ║
    // ╚══════════════════════════════════════════════════════════════════╝
    const reconciledToFree =
      String(result?.status ?? result?.Status ?? "").toLowerCase() ===
      "cancelled";

    if (reconciledToFree) {
      await showSuccessToast(
        result?.message ||
          result?.Message ||
          "Assinatura cancelada e plano Free atribuído."
      );
    }
    // Pausa normal: sem toast — comportamento original preservado.
  } catch (err) {
    showErrorToast(
      err?.message || "Não foi possível pausar a renovação. Tente novamente."
    );
  } finally {
    isPausing.value = false;
  }
}

// ═══════════════════════════════════════════════════════════════
// Reativação de assinatura PAUSADA → AUTHORIZED
// Acionado pelo botão "Reativar" que aparece no card quando
// preapprovalStatus === "paused"
// ═══════════════════════════════════════════════════════════════
const showReactivateConfirm = ref(false);
const isReactivating = ref(false);

async function handleConfirmReactivate() {
  // ✅ ALTERADO: bloqueia se qualquer ação do componente estiver em andamento.
  if (isAnyAccountActionRunning.value) return;

  isReactivating.value = true;
  showReactivateConfirm.value = false;

  try {
    // ✅ MUDANÇA: captura o DTO retornado. O backend agora responde HTTP 200
    // tanto na reativação normal quanto na reconciliação (assinatura já
    // cancelada direto no painel do MP). Os dois casos se distinguem pelo
    // campo Status:
    //   - reativação normal  → Status === "Active"    (sub volta a ativa)
    //   - reconciliação→Free → Status === "Cancelled"
    const result = await subscriptionStore.reactivateActiveSubscription({
      reason: "Reativação da renovação solicitada pelo usuário",
    });
    await fetchData(true);

    // ✅ NOVO: reativação altera o estado da assinatura — avisa o pai.
    emit("subscriptionChanged");

    // ╔══════════════════════════════════════════════════════════════════╗
    // ║ ✅ NOVO (reconciliação de reativação sobre assinatura cancelada): ║
    // ║                                                                  ║
    // ║ Quando a assinatura já foi cancelada direto no painel do MP, o    ║
    // ║ backend não consegue reativá-la: reconcilia o estado para         ║
    // ║ Cancelled + Free + crédito proporcional e retorna o DTO com       ║
    // ║ Status="Cancelled" (HTTP 200). Para o usuário isso encerra a      ║
    // ║ assinatura e devolve o crédito — um resultado que precisa ser     ║
    // ║ comunicado, ao contrário da reativação normal (silenciosa).       ║
    // ║                                                                  ║
    // ║ Detecção (abordagem A): reativação NORMAL retorna Status="Active";║
    // ║ só a reconciliação retorna "Cancelled".                           ║
    // ║                                                                  ║
    // ║ Comportamento:                                                   ║
    // ║   - reativação normal → nenhum toast (o sumiço do estado          ║
    // ║                         "pausada" no card já é feedback).         ║
    // ║   - reconciliação     → toast de SUCESSO com a mensagem do        ║
    // ║                         backend.                                 ║
    // ╚══════════════════════════════════════════════════════════════════╝
    const reconciledToFree =
      String(result?.status ?? result?.Status ?? "").toLowerCase() ===
      "cancelled";

    if (reconciledToFree) {
      await showSuccessToast(
        result?.message ||
          result?.Message ||
          "Assinatura cancelada e plano Free atribuído."
      );
    }
    // Reativação normal: sem toast — comportamento original preservado.
  } catch (err) {
    showErrorToast(
      err?.message || "Não foi possível reativar a assinatura. Tente novamente."
    );
  } finally {
    isReactivating.value = false;
  }
}

const managedAccountsUsage = computed(() => {
  const usage = managedAccountStore.aggregatedUsage;
  if (!usage) return "... / ... contas";
  return `${formatNum(usage.activeManagedAccounts)} / ${formatLimit(
    usage.maxManagedAccounts
  )} contas`;
});

const showManagedAccountsCard = computed(() => {
  const usage = managedAccountStore.aggregatedUsage;
  return (
    !showManagedContextHint.value && !!usage && usage.maxManagedAccounts > 0
  );
});

function normalizeAvatarPath(path) {
  if (!path) return "";

  if (path.startsWith("http://") || path.startsWith("https://")) return path;

  const base = api.defaults.baseURL.replace(/\/api$/, "").replace(/\/+$/, "");
  const cleanPath = path.startsWith("/") ? path : `/${path}`;

  return `${base}${cleanPath}`;
}

function getAvatarUrl(photoPath) {
  if (!photoPath) return "";
  return normalizeAvatarPath(photoPath);
}

function resolveCurrentUserPhotoPath(user) {
  if (!user) return null;

  return (
    user.photoPath ||
    user.photoUrl ||
    user.userPhotoPath ||
    user.userPhotoUrl ||
    user.photo_path ||
    user.user_photo_path ||
    null
  );
}

function resetToastTimeout(duration) {
  return new Promise((resolve) => {
    if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
    toastTimeoutId.value = setTimeout(() => {
      showToast.value = false;
      toastTimeoutId.value = null;
      resolve();
    }, duration);
  });
}

function showErrorToast(message) {
  toastType.value = "error";
  toastMessage.value = message;
  showToast.value = true;
  resetToastTimeout(6000);
}

async function showSuccessToast(message) {
  toastType.value = "success";
  toastMessage.value = message;
  showToast.value = true;
  await resetToastTimeout(6000);
}
// ====================================================================

const currentUser = computed(() => userStore.currentUser);
const activeSubscription = computed(() => subscriptionStore.activeSubscription);

// --------- FUNÇÕES DE FORMATAÇÃO ---------

const formatNum = (num) =>
  typeof num === "number" ? num.toLocaleString("pt-BR") : "...";

const formatDecimal = (num) => {
  if (typeof num !== "number") return "...";
  if (num === 0) return "0";
  if (num > 0 && num < 1) return num.toFixed(3).replace(".", ",");
  return Math.floor(num).toLocaleString("pt-BR");
};

const formatLimit = (limit) =>
  limit === -1 || limit === null || typeof limit === "undefined"
    ? "Ilimitado"
    : formatNum(limit);

const charsToPages = (chars) => (chars > 0 ? chars / CHARS_PER_PAGE : 0);

const formatLimitInPages = (limit) => {
  if (limit === -1 || limit === null || typeof limit === "undefined") {
    return "Ilimitado";
  }
  return formatDecimal(charsToPages(limit));
};

const calculatePercentage = (current, max) => {
  if (max === -1 || !max || max === 0) return 0;
  return Math.min(100, Math.round((current / max) * 100));
};

// --------- COMPUTEDS DE INFORMAÇÃO BÁSICA ---------

const email = computed(() => currentUser.value?.email ?? "Carregando...");
const userName = computed(() => currentUser.value?.userName ?? "Carregando...");

const memberSince = computed(() => {
  const createdAt = currentUser.value?.createdAt;
  return createdAt
    ? format(new Date(createdAt), "dd/MM/yyyy")
    : "Carregando...";
});

// ✅ NOVO: saldo de crédito do usuário.
// Exibe users.credit_balance retornado pelo backend em UserReadDto.
// Aceita camelCase, snake_case e PascalCase para manter compatibilidade.
const formattedCreditBalance = computed(() => {
  const raw =
    currentUser.value?.creditBalance ??
    currentUser.value?.credit_balance ??
    currentUser.value?.CreditBalance ??
    0;

  const parsed =
    typeof raw === "number" ? raw : Number(String(raw).replace(",", "."));

  const safeValue = Number.isFinite(parsed) ? parsed : 0;

  return safeValue.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
});

// ✅ NOVO: hint em duas linhas para o tooltip nativo do navegador.
const creditBalanceHint =
  "Crédito proporcional gerado ao trocar ou \ncancelar um plano pago antes do fim do ciclo.";

// ✅ NOVO: versão linear para acessibilidade.
const creditBalanceAriaLabel =
  "Crédito proporcional gerado ao trocar ou cancelar um plano pago antes do fim do ciclo.";

const purgeDateFormatted = computed(() => {
  return format(addDays(new Date(), 30), "dd/MM/yyyy");
});

const planName = computed(() => currentUser.value?.planName ?? "Carregando...");

// --------- FUNÇÃO DE GRADIENTE DINÂMICO ---------

const getDynamicGradient = () => {
  const gradientStops = ["#10b981 0%", "#facc15 50%", "#ef4444 100%"].join(
    ", "
  );
  return `linear-gradient(to right, ${gradientStops})`;
};

const showManagedContextHint = computed(
  () => currentUser.value?.loginContext === "managed"
);

// ✅ NOVO: bloqueio global dos botões de ação do componente.
// Enquanto qualquer ação assíncrona estiver em execução,
// todos os botões ficam desabilitados para evitar múltiplos cliques.
const isAnyAccountActionRunning = computed(
  () =>
    isDeactivating.value ||
    isCancelling.value ||
    isPausing.value ||
    isReactivating.value ||
    isRefunding.value
);

// --------- COMPUTEDS DE USO DE RECURSOS ---------

const notesUsage = computed(() => {
  const u = currentUser.value;
  if (!u) return { percentage: 0, displayPages: "... / ..." };

  const currentChars =
    managedAccountStore.aggregatedUsage?.totalActiveNotesCount ??
    u.activeNotesCount ??
    0;

  const maxChars = u.maxNoteCharacters ?? -1;
  return {
    currentChars,
    maxChars,
    percentage: calculatePercentage(currentChars, maxChars),
    displayChars: `${formatNum(currentChars)} / ${formatLimit(
      maxChars
    )} caracteres`,
    displayPages: `~ ${formatDecimal(
      charsToPages(currentChars)
    )} / ${formatLimitInPages(maxChars)} páginas`,
    gradientStyle: getDynamicGradient(),
  };
});

const textsUsage = computed(() => {
  const u = currentUser.value;
  if (!u) return { percentage: 0, displayPages: "... / ..." };

  const currentChars =
    managedAccountStore.aggregatedUsage?.totalActiveTextsCount ??
    u.activeTextsCount ??
    0;

  const maxChars = u.maxTextCharacters ?? -1;
  return {
    currentChars,
    maxChars,
    percentage: calculatePercentage(currentChars, maxChars),
    displayChars: `${formatNum(currentChars)} / ${formatLimit(
      maxChars
    )} caracteres`,
    displayPages: `~ ${formatDecimal(
      charsToPages(currentChars)
    )} / ${formatLimitInPages(maxChars)} páginas`,
    gradientStyle: getDynamicGradient(),
  };
});

const groupUseCount = computed(() => {
  const u = currentUser.value;
  if (!u) return "... / ... usuários";

  const current =
    managedAccountStore.aggregatedUsage?.totalGroupUserCount ??
    u.groupUserCount ??
    0;

  return `${formatNum(current)} / ${formatLimit(u.maxGroupUser)} usuários`;
});

// --------- COMPUTEDS DE ASSINATURA ---------

const isRecurringPlan = computed(() => {
  if (!activeSubscription.value) return false;
  return (
    activeSubscription.value.isRecurring ||
    !!activeSubscription.value.mpSubscriptionId
  );
});

const isCancelButtonDisabled = computed(() => {
  if (!activeSubscription.value) return false;
  return activeSubscription.value.cancelAtPeriodEnd === true;
});

const isPaused = computed(() => {
  const s = String(activeSubscription.value?.preapprovalStatus ?? "")
    .trim()
    .toLowerCase();
  return s === "paused" || s === "4";
});

// "Pausar renovação ao fim do período" só faz sentido se o ciclo atual
// foi efetivamente pago. Quando PaymentStatus ainda é Pending (autorização
// inicial sem 1º payment.approved confirmado), não há "período pago" do
// qual a renovação possa ser pausada — só oferecemos cancelamento imediato.
// Alinhado com a validação backend em CancelSubscriptionAsync.
const canPauseAtPeriodEnd = computed(() => {
  if (!activeSubscription.value) return false;
  const ps = String(activeSubscription.value.paymentStatus ?? "")
    .trim()
    .toLowerCase();
  return ps === "approved";
});

// ✅ NOVO: visibilidade unificada do botão "Cancelar assinatura".
//
// Substitui a condição antiga "v-if=isRecurringPlan && !managed", que
// ocultava o botão para subs avulsas (PIX, cartão único, upgrade_credit).
// Toda sub paga que esteja em activeSubscription pode ser cancelada —
// recorrente OU avulsa.
//
// Guarda contra plano Free: o backend mantém registro de subscription
// para o plano Free (sub "always-on" que segura o acesso gratuito após
// downgrade/expiração), então essa entrada aparece em activeSubscription.
// Não faz sentido cancelar Free — o usuário ficaria sem nenhuma sub.
// Por isso bloqueamos explicitamente via Plan.Name.
//
// Para subs avulsas pagas: openCancelConfirm cai em "active-paid" (isPaused
// = false, canPauseAtPeriodEnd = true quando paymentStatus = "approved"),
// e o CancelImmediateConfirmModal nessa variante mostra texto neutro
// adequado ("será encerrada imediatamente... crédito proporcional se houver").
const canCancelSubscription = computed(() => {
  if (!activeSubscription.value) return false;
  if (currentUser.value?.loginContext === "managed") return false;

  // Detecta plano gratuito pelo nome: "Free" e "Edu Free" (a escola nasce
  // nele). O DTO da assinatura não traz o preço, então a regra é o nome
  // terminar em "free". Tenta tanto activeSubscription.planName quanto
  // currentUser.planName - em alguns fluxos o planName fica em um lugar, em
  // outros, no outro, dependendo de qual store atualizou primeiro.
  const isFreePlanName = (name) =>
    /(^|\s)free$/i.test(String(name ?? "").trim());

  if (
    isFreePlanName(activeSubscription.value.planName) ||
    isFreePlanName(currentUser.value?.planName)
  ) {
    return false;
  }

  return true;
});

const formattedEndDate = computed(() => {
  const end = activeSubscription.value?.endDate;
  return end ? formatDateTimeForDisplay(end) : null;
});

// --------- >>> NOVO: LÓGICA DE ELEGIBILIDADE DE ESTORNO --------- //

// Estado da elegibilidade
// <<< Mudança A: o DTO agora inclui também paymentMethod, refundAmount e
// creditBalanceRefundAmount, usados pelo RefundConfirmModal.
const refundEligibility = ref(null); // RefundEligibilityDto | null
const isCheckingEligibility = ref(false);

// <<< NOVO: verifica elegibilidade assim que há uma assinatura ativa
async function checkRefundEligibility() {
  const sub = activeSubscription.value;

  // Só faz sentido para assinaturas avulsas ou primeiro ciclo de recorrentes
  // com pagamento aprovado. A verificação completa (uso + janela) fica no backend.
  if (!sub || sub.paymentStatus?.toLowerCase() !== "approved") {
    refundEligibility.value = null;
    return;
  }

  isCheckingEligibility.value = true;
  try {
    refundEligibility.value = await subscriptionStore.checkRefundEligibility(
      sub.subscriptionId
    );
  } catch {
    // Em caso de erro na verificação, simplesmente não exibe o botão.
    refundEligibility.value = null;
  } finally {
    isCheckingEligibility.value = false;
  }
}

// <<< NOVO: exibe o botão somente quando backend confirmou elegibilidade
const showRefundButton = computed(
  () =>
    !showManagedContextHint.value &&
    !!activeSubscription.value?.endDate &&
    refundEligibility.value?.eligible === true
);

// <<< NOVO: modal de confirmação de estorno
const showRefundConfirm = ref(false);
const isRefunding = ref(false);

// Modal de resultado do estorno — cobre os três caminhos:
//   1. Falha HTTP / rede                  → variant="warn"
//   2. Backend OK + refundSucceeded=false → variant="warn"
//   3. Backend OK + refundSucceeded=true  → variant="success"
const showRefundResult = ref(false);
const refundResultMessage = ref("");
const refundResultVariant = ref("warn"); // "warn" | "success"

async function handleRefundAndDowngrade() {
  const sub = activeSubscription.value;

  // ✅ ALTERADO: bloqueia se qualquer ação do componente estiver em andamento.
  if (!sub || isAnyAccountActionRunning.value) return;

  isRefunding.value = true;
  showRefundConfirm.value = false;

  try {
    const result = await subscriptionStore.refundAndDowngrade(
      sub.subscriptionId
    );

    // Backend pode retornar HTTP 200 com refundSucceeded=false quando o MP
    // recusa o estorno. Nesse caso o estorno NÃO aconteceu e a assinatura
    // continua ativa e elegível — o botão "Solicitar reembolso" deve
    // permanecer disponível para uma nova tentativa.
    const succeeded = result?.refundSucceeded === true;

    await fetchData(true);

    // ✅ NOVO: quando o estorno de fato ocorreu, a assinatura virou Free —
    // avisa o pai para recalcular o badge de plano do header.
    if (succeeded) {
      emit("subscriptionChanged");
    }

    // Só zera a elegibilidade quando o estorno de fato ocorreu (sub virou
    // Free, não há mais o que estornar). Em caso de falha, o fetchData acima
    // já repopulou refundEligibility com o estado correto — não sobrescrever.
    if (succeeded) {
      refundEligibility.value = null;
    }

    refundResultMessage.value =
      result?.message ||
      (succeeded
        ? "Estorno realizado com sucesso."
        : "Estorno não pôde ser processado automaticamente.");
    refundResultVariant.value = succeeded ? "success" : "warn";
    showRefundResult.value = true;
  } catch (err) {
    // Caminho 1: falha de comunicação real (HTTP error, rede, token expirado).
    // Backend não chegou a processar nada — usuário pode tentar de novo.
    refundResultMessage.value =
      err?.message || "Não foi possível processar o estorno. Tente novamente.";
    refundResultVariant.value = "warn";
    showRefundResult.value = true;
  } finally {
    isRefunding.value = false;
  }
}

// --------- FIM DA LÓGICA DE ESTORNO --------- //

// --------- FUNÇÃO CENTRAL DE RELOAD ---------

async function fetchData(forceUserReload = false) {
  isLoading.value = true;
  try {
    const loginContext = userStore.currentUser?.loginContext ?? "owner";

    if (forceUserReload || !currentUser.value) {
      await userStore.refreshCurrentUser();
    }

    if (loginContext === "managed") {
      await managedAccountStore.fetchAggregatedUsage();
      return;
    }

    await userStore.fetchCurrentUserSubscription();
    if (currentUser.value?.userId) {
      await subscriptionStore.fetchSubscriptionsPaged(
        1,
        100,
        currentUser.value.userId
      );
    }

    await managedAccountStore.fetchManagedAccounts();
    await managedAccountStore.fetchAggregatedUsage();

    await checkRefundEligibility();
  } finally {
    isLoading.value = false;
  }
}

onMounted(() => fetchData(true));

// ✅ NOVO: recarrega tudo quando o pai sinaliza que o UpgradeModal fechou.
//
// Ignoramos o disparo inicial (oldVal === undefined) porque onMounted já
// fez a primeira carga; só reagimos a mudanças subsequentes do contador.
watch(
  () => props.reloadSignal,
  (newVal, oldVal) => {
    if (oldVal === undefined) return;
    if (newVal !== oldVal) {
      fetchData(true);
    }
  }
);

// ✅ NOVO (gatilho reativo principal):
//
// Observa o planId da assinatura ativa diretamente da subscriptionStore.
// Essa é a MESMA cadeia reativa que faz o botão "Cancelar assinatura" aparecer
// corretamente após um upgrade:
//
//   subscriptionStore.subscriptions muda
//       ↓
//   getter activeSubscription recomputa
//       ↓
//   isRecurringPlan recomputa → botão Cancelar aparece
//
// Acoplamos a sincronização de currentUser (planName/cotas) na mesma cadeia.
// Sempre que o planId da assinatura ativa mudar, chamamos refreshCurrentUser
// e fetchCurrentUserSubscription para que userStore.currentUser passe a refletir
// o novo plano (planName, maxNoteCharacters, maxTextCharacters, maxGroupUser).
//
// Por que planId e não a assinatura inteira?
// - planId muda apenas em upgrade/downgrade reais; usar o objeto inteiro
//   dispararia em qualquer mexida sem importância (ex.: refresh sem troca).
// - Para o caso "cancelei e voltei para Free", o planId também muda
//   (vira o planId do Free), então a sincronização ocorre naturalmente.
//
// O disparo inicial (oldVal === undefined) é ignorado porque onMounted já
// chamou fetchData(true) e cuidou da primeira carga.
watch(
  () => subscriptionStore.activeSubscription?.planId ?? null,
  async (newPlanId, oldPlanId) => {
    if (oldPlanId === undefined) return;
    if (newPlanId === oldPlanId) return;

    // Não usamos fetchData(true) inteiro aqui porque ele já é chamado por
    // outros caminhos (onMounted, reloadSignal, ações de cancel/pause/etc.)
    // e re-disparar subscriptionStore.fetchSubscriptionsPaged() iria criar
    // loop neste próprio watcher. Em vez disso, sincronizamos só o que falta:
    // currentUser (plano + cotas) e o agregado de consumo.
    isLoading.value = true;
    try {
      await userStore.refreshCurrentUser();
      await userStore.fetchCurrentUserSubscription();

      // Reavalia elegibilidade de reembolso — a nova assinatura pode ou não
      // ser elegível, e o estado anterior (de outra subscriptionId) ficou stale.
      await checkRefundEligibility();
    } catch (err) {
      console.error(
        "[AccountSummary] Falha ao sincronizar currentUser após troca de plano:",
        err
      );
    } finally {
      isLoading.value = false;
    }
  }
);

// --------- DESATIVAÇÃO DE CONTA ---------

async function confirmDeactivate(callback) {
  // ✅ ALTERADO: bloqueia se qualquer ação do componente estiver em andamento.
  if (isAnyAccountActionRunning.value) return;

  isDeactivating.value = true;

  try {
    await userStore.deactivateMyAccount();
    await callback(true);
    window.location.reload();
  } catch (err) {
    callback(false);
    showErrorToast(err?.message || "Não foi possível desativar sua conta.");
  } finally {
    isDeactivating.value = false;
  }
}

onUnmounted(() => {
  if (toastTimeoutId.value) clearTimeout(toastTimeoutId.value);
});

function handleOpenUpgradeModal(event = null) {
  // ✅ PATCH: Proteção adicional contra bubbling quando abre o upgrade a partir do AccountSummary
  if (event) {
    event?.preventDefault?.();
    event?.stopImmediatePropagation?.();
  }

  // evita abrir modal de troca de plano durante outra ação.
  if (isAnyAccountActionRunning.value) return;

  emit("openUpgradeModal");
}
</script>

<template>
  <div class="summary-container">
    <h3 class="view-title">Resumo da Conta</h3>
    <p class="view-subtitle">
      Uma visão geral das informações relacionadas a sua conta.
    </p>

    <div class="summary-grid">
      <!-- ✅ Overlay de carregamento -->
      <transition name="loading-fade">
        <div
          v-if="isLoading"
          class="loading-overlay"
          aria-label="Carregando..."
        >
          <svg class="loading-spinner" viewBox="0 0 24 24" fill="none">
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            />
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            />
          </svg>
          <span class="loading-text">Carregando...</span>
        </div>
      </transition>
      <div class="account-row-full">
        <div class="summary-card">
          <div class="card-icon account-avatar-icon-wrapper">
            <span
              v-if="getAvatarUrl(resolveCurrentUserPhotoPath(currentUser))"
              class="account-avatar-img"
              :style="{
                backgroundImage: `url('${getAvatarUrl(
                  resolveCurrentUserPhotoPath(currentUser)
                )}')`,
              }"
            ></span>

            <i v-else class="fas fa-user"></i>
          </div>

          <div class="card-content">
            <div class="account-label-row">
              <span v-if="!showManagedContextHint" class="card-label"
                >Conta</span
              >

              <span
                v-else
                class="account-member-badge user-plan--managed"
                :title="`Conta Membro de ${currentUser?.managerName}`"
              >
                <i class="fas fa-user-friends user-plan--managed-icon"></i>
                <span class="user-plan--managed-text">
                  Conta membro de
                  <strong class="user-plan--managed-name">{{
                    currentUser?.managerName
                  }}</strong>
                </span>
              </span>
            </div>

            <span class="card-value">{{ userName }}</span>
            <span class="card-subtext">
              {{ email }}
              <span class="card-subtext__sep">•</span>
              Membro desde {{ memberSince }}

              <!-- ✅ NOVO: crédito destacado discretamente na mesma linha -->
              <span class="card-subtext__sep">•</span>
              <!-- ✅ NOVO: hint curto explicando o crédito proporcional -->
              <!-- ✅ NOVO: hint curto em duas linhas explicando o crédito proporcional -->
              <span
                class="credit-balance-chip"
                :title="creditBalanceHint"
                :aria-label="creditBalanceAriaLabel"
              >
                Crédito: {{ formattedCreditBalance }}
              </span>
            </span>
          </div>

          <div class="danger-actions ml-auto">
            <button
              type="button"
              class="btn btn-chip-danger"
              :disabled="isAnyAccountActionRunning"
              @click.prevent="showDeactivateMenu = true"
            >
              Excluir Conta
            </button>
          </div>
        </div>
      </div>

      <!-- Bloco do plano — agora ocupa toda a largura.
     Member-card foi removido; "Membro desde" migrou para o card Conta acima. -->
      <div class="plan-row-full">
        <div class="summary-card plan-card plan-card--stacked">
          <div class="card-icon">
            <i class="fas fa-star"></i>
          </div>

          <div class="card-content">
            <div class="plan-title-row">
              <span class="card-label">Plano Atual</span>

              <!-- Badge inline de pausada (substitui o paused-alert antigo) -->
              <span
                v-if="isPaused"
                class="plan-paused-chip"
                title="Renovação automática pausada"
              >
                <i class="fas fa-pause-circle"></i>
                <span>Pausada</span>
              </span>
            </div>

            <span v-if="!showManagedContextHint" class="card-value">{{
              planName
            }}</span>

            <p v-if="showManagedContextHint" class="managed-context-hint">
              Você está consumindo a cota do plano
              <strong>{{ currentUser?.planName }}</strong
              >, do usuário titular
              <strong>{{ currentUser?.managerName }}</strong
              >.
            </p>

            <!-- Subtexto: muda conforme o estado. Pausada → "acesso garantido até".
           Ativa → "Válido até". Sem endDate → "tempo indeterminado". -->
            <span
              v-if="currentUser?.loginContext !== 'managed'"
              class="card-subtext"
            >
              <template v-if="isPaused && formattedEndDate">
                Acesso garantido até {{ formattedEndDate }}
              </template>
              <template v-else-if="formattedEndDate">
                Válido até {{ formattedEndDate }}
              </template>
              <template v-else> Válido por tempo indeterminado </template>
            </span>
          </div>

          <div class="plan-actions plan-actions--row">
            <!-- ═══════════════════════════════════════════════════════════════
       Botões de ação do plano — todos diretos, sem dropdown.
       Cada estado da assinatura habilita um conjunto coerente:

         ATIVA + paga    : [Pausar renovação] [Cancelar] [Atualizar Plano] [Reembolso?]
         ATIVA + pending : [Cancelar] [Atualizar Plano]
         PAUSADA         : [Reativar] [Cancelar] [Atualizar Plano] [Reembolso?]
         MANAGED         : (nenhum)

       ✅ ALTERADO:
       Todos os botões agora usam isAnyAccountActionRunning.
       Assim, quando qualquer ação estiver executando, todos ficam bloqueados.
  ═══════════════════════════════════════════════════════════════ -->

            <!-- Reativar — só quando pausada -->
            <button
              v-if="
                isRecurringPlan &&
                isPaused &&
                currentUser?.loginContext !== 'managed'
              "
              type="button"
              class="btn btn-chip-reactivate"
              :disabled="isAnyAccountActionRunning"
              @click.prevent="showReactivateConfirm = true"
              title="Reativar a renovação automática"
            >
              <i
                v-if="isReactivating"
                class="btn-spinner btn-spinner--green"
              ></i>
              <i v-else class="fas fa-play-circle"></i>
              <span>{{ isReactivating ? "Reativando..." : "Reativar" }}</span>
            </button>

            <!-- Pausar renovação — só quando ativa e paga (não-pausada, não-pending) -->
            <button
              v-if="
                isRecurringPlan &&
                !isPaused &&
                canPauseAtPeriodEnd &&
                currentUser?.loginContext !== 'managed'
              "
              type="button"
              class="btn btn-chip-pause"
              :disabled="isAnyAccountActionRunning"
              @click.prevent="showPauseConfirm = true"
              title="Interromper a renovação automática ao fim do período"
            >
              <i v-if="isPausing" class="btn-spinner btn-spinner--orange"></i>
              <i v-else class="fas fa-pause-circle"></i>
              <span>{{ isPausing ? "Pausando..." : "Pausar renovação" }}</span>
            </button>

            <!-- ✅ MUDANÇA: Cancelar assinatura — disponível para qualquer sub paga (recorrente OU avulsa) não-managed -->
            <button              
              v-if="canCancelSubscription"
              type="button"
              class="btn btn-chip-danger"
              :disabled="isCancelButtonDisabled || isAnyAccountActionRunning"
              @click.prevent="openCancelConfirm"
              title="Encerrar a assinatura imediatamente"
            >
              <i class="fa-solid fa-user-slash"></i>
              <span>Cancelar assinatura</span>
            </button>

            <!-- Atualizar Plano — sempre disponível para non-managed -->
            <button
              v-if="currentUser?.loginContext !== 'managed'"
              type="button"
              @click.stop.prevent="handleOpenUpgradeModal"
              class="btn btn-chip-secondary btn-change-plan"
              :disabled="isAnyAccountActionRunning"
            >
              <i class="fas fa-rocket"></i>
              <span class="btn-change-plan__text">Atualizar Plano</span>
            </button>

            <!-- Solicitar reembolso — só quando backend confirma elegibilidade -->
            <button
              v-if="showRefundButton"
              type="button"
              class="btn btn-chip-refund"
              :disabled="isAnyAccountActionRunning"
              @click.prevent="showRefundConfirm = true"
            >
              <i v-if="isRefunding" class="btn-spinner"></i>
              <i v-else class="fas fa-undo-alt"></i>
              <span>{{
                isRefunding ? "Processando..." : "Solicitar reembolso"
              }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- TEXTOS -->
      <div class="summary-card usage-card">
        <div class="card-icon">
          <i class="fas fa-book-open"></i>
        </div>
        <div class="card-content">
          <span class="card-label">
            Cota de Textos
            <span v-if="showManagedContextHint">
              do <strong>Titular</strong></span
            >
          </span>
          <span class="card-value">{{ textsUsage.displayPages }}</span>
          <span class="card-subtext">{{ textsUsage.displayChars }}</span>

          <div class="progress-bar-container">
            <div
              class="progress-bar-fill"
              :style="{
                background: textsUsage.gradientStyle,
                '--clip-right': 100 - textsUsage.percentage + '%',
              }"
            ></div>
            <div
              class="progress-bar-mask"
              :style="{ width: 100 - textsUsage.percentage + '%' }"
            ></div>
          </div>

          <span v-if="textsUsage.maxChars !== -1" class="percentage-label">
            {{ textsUsage.percentage }}% Usado
          </span>
          <span v-else class="percentage-label">Uso ilimitado</span>
        </div>
      </div>

      <!-- NOTAS -->
      <div class="summary-card usage-card">
        <div class="card-icon">
          <i class="fas fa-sticky-note"></i>
        </div>
        <div class="card-content">
          <span class="card-label">
            Cota de Notas
            <span v-if="showManagedContextHint">
              do <strong>Titular</strong></span
            >
          </span>
          <span class="card-value">{{ notesUsage.displayPages }}</span>
          <span class="card-subtext">{{ notesUsage.displayChars }}</span>

          <div class="progress-bar-container">
            <div
              class="progress-bar-fill"
              :style="{
                background: notesUsage.gradientStyle,
                '--clip-right': 100 - notesUsage.percentage + '%',
              }"
            ></div>
            <div
              class="progress-bar-mask"
              :style="{ width: 100 - notesUsage.percentage + '%' }"
            ></div>
          </div>

          <span v-if="notesUsage.maxChars !== -1" class="percentage-label">
            {{ notesUsage.percentage }}% Usado
          </span>
          <span v-else class="percentage-label">Uso ilimitado</span>
        </div>
      </div>

      <div class="summary-card" v-if="!showManagedContextHint">
        <div class="card-icon"><i class="fas fa-users"></i></div>
        <div class="card-content">
          <span class="card-label">
            Cota de Usuários Vinculadas a Grupos
            <span v-if="showManagedContextHint">
              do <strong>Titular</strong></span
            >
          </span>
          <span class="card-value">{{ groupUseCount }}</span>
        </div>
      </div>
      <div class="summary-card" v-if="showManagedAccountsCard">
        <div class="card-icon">
          <i class="fas fa-user-friends"></i>
        </div>
        <div class="card-content">
          <span class="card-label">Cota de Contas Membro</span>
          <span class="card-value">{{ managedAccountsUsage }}</span>
        </div>
      </div>
    </div>
  </div>

  <Teleport to="body">
    <DeactivationConfirmModal
      v-if="showDeactivateMenu"
      :purgeDate="purgeDateFormatted"
      @close="showDeactivateMenu = false"
      @confirm="confirmDeactivate"
    />

    <CancelImmediateConfirmModal
      v-if="showCancelConfirm"
      :is-cancelling="isCancelling"
      :variant="cancelVariant"
      @close="showCancelConfirm = false"
      @confirm="handleConfirmCancel"
    />

    <PauseSubscriptionConfirmModal
      v-if="showPauseConfirm"
      :is-pausing="isPausing"
      :end-date-formatted="formattedEndDate"
      @close="showPauseConfirm = false"
      @confirm="handleConfirmPause"
    />

    <ReactivateSubscriptionConfirmModal
      v-if="showReactivateConfirm"
      :is-reactivating="isReactivating"
      :end-date-formatted="formattedEndDate"
      @close="showReactivateConfirm = false"
      @confirm="handleConfirmReactivate"
    />

    <RefundConfirmModal
      v-if="showRefundConfirm"
      :is-refunding="isRefunding"
      :eligibility-message="refundEligibility?.message"
      :refund-amount="refundEligibility?.refundAmount"
      :payment-method="refundEligibility?.paymentMethod"
      :credit-balance-refund-amount="
        refundEligibility?.creditBalanceRefundAmount
      "
      @close="showRefundConfirm = false"
      @confirm="handleRefundAndDowngrade"
    />

    <RefundResultModal
      :show="showRefundResult"
      :message="refundResultMessage"
      :variant="refundResultVariant"
      @close="showRefundResult = false"
    />

    <transition name="toast-fade">
      <div
        v-if="showToast"
        class="fixed inset-0 pointer-events-none z-[99999] flex items-start justify-center pt-6 px-4"
      >
        <div
          :class="[
 'toast pointer-events-auto',
            toastType === 'error' ? 'toast-error' : 'toast-success',
          ]"
        >
          <div class="toast-content">
            <p class="toast-title">
              {{ toastType === "error" ? "Atenção" : "Sucesso!" }}
            </p>
            <p class="toast-message">{{ toastMessage }}</p>
          </div>
          <button
            type="button"
            class="toast-close"
            :disabled="isAnyAccountActionRunning"
            @click="showToast = false"
          >
            ×
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
.summary-container {
  padding: 0.3rem;
  height: 100%;
  position: relative;
}

.view-title {
  font-size: 1.15rem;
  font-weight: 600;
  color: var(--text-dark);
  margin: 0 0 0.35rem 0;
}

.view-subtitle {
  font-size: 0.9rem;
  color: var(--text-light);
  margin-top: 0.2rem;
  margin-bottom: 1.2rem;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.9rem;
}

.account-row-full {
  grid-column: 1 / -1;
}

.summary-card {
  background-color: var(--bg-header);
  border: 1px solid var(--border-color);
  border-radius: 12px;
  padding: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  position: relative;
  height: 100%;
}

.card-icon {
  flex-shrink: 0;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: var(--brand-soft);
  color: #4338ca;
  font-size: 1.1rem;
}

.card-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.card-label {
  font-size: 0.82rem;
  color: var(--text-light);
}

.card-value {
  font-size: 1.02rem;
  font-weight: 600;
  color: var(--text-dark);
  word-break: break-word;
}

.card-subtext {
  font-size: 0.82rem;
  color: var(--text-light);
  margin-top: 0.2rem;
}

.plan-actions,
.danger-actions {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  margin-left: auto;
}

.ml-auto {
  margin-left: auto;
}

/* Mantém todos os botões do plano na mesma linha */
.plan-actions--row {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: flex-start;
  gap: 0.35rem;
  margin-left: auto;
  min-width: 0;
  overflow: hidden;
}

.btn {
  flex-shrink: 0;
}

.btn > i {
  width: 0.78rem;
}

/* Atualizar Plano não tem mais ajuste próprio de altura/posição.
   Ele herda exatamente o mesmo alinhamento dos demais botões via .btn. */
.btn-change-plan {
  line-height: 1;
}

.btn-change-plan__text {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.btn-chip-secondary {
  background-color: var(--warning-bg);
  color: var(--warning-text);
  border: 1px solid #facc15;
}

.btn-chip-secondary:hover:not(:disabled) {
  background-color: #fef08a;
}

.btn-chip-danger {
  background-color: var(--danger-bg);
  color: var(--danger-text);
  border: 1px solid var(--danger-bg);
}

.btn-chip-danger:hover:not(:disabled) {
  background-color: var(--danger-bg);
}

.btn-chip-refund {
  background-color: #fff7ed;
  color: #9a3412;
  border: 1px solid #fed7aa;
}

.btn-chip-refund:hover:not(:disabled) {
  background-color: #ffedd5;
}

.btn-spinner {
  display: inline-block;
  width: 13px;
  height: 13px;
  border: 2px solid #fed7aa;
  border-top-color: #9a3412;
  border-radius: 50%;
  animation: btn-spin 0.7s linear infinite;
  flex-shrink: 0;
}

@keyframes btn-spin {
  to {
    transform: rotate(360deg);
  }
}

.progress-bar-container {
  width: 100%;
  height: 8px;
  border-radius: 4px;
  margin-top: 0.65rem;
  background: var(--neutral-200);
  overflow: hidden;
  position: relative;
}

.progress-bar-fill {
  position: absolute;
  inset: 0;
  clip-path: inset(0 var(--clip-right) 0 0);
}

@media (max-width: 850px) {
  .summary-grid {
    grid-template-columns: 1fr;
  }
}

.toast {
  width: 420px;
}

.toast-title {
  margin-bottom: 0.2rem;
}

.toast-close {
  margin-left: auto;
}

.fixed {
  position: fixed;
  inset: 0;
}

.z-\[99999\] {
  z-index: 99999;
}

.percentage-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--text-light);
  margin-top: 0.25rem;
  display: block;
}

.managed-context-hint {
  margin-top: 0.15rem;
  font-size: 0.82rem;
  color: var(--text-light);
  line-height: 1.45;
  text-align: left;
  background: none;
  border: none;
  padding: 0;
  width: 100%;
}

.managed-context-hint strong {
  font-weight: 600;
  color: var(--text-dark);
}

/* Avatar real no card Conta */
.account-avatar-icon-wrapper {
  overflow: hidden;
  padding: 0;
}

.account-avatar-img {
  width: 100%;
  height: 100%;
  display: block;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

.account-member-badge {
  margin-left: 0rem;
}

.user-plan--managed {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  max-width: 270px;
  background: var(--warning-bg);
  color: var(--warning-text);
  border: 1px solid #fcd34d;
  border-radius: 999px;
  padding: 3px 10px 3px 8px;
  white-space: nowrap;
  overflow: hidden;
}

.user-plan--managed-icon {
  flex-shrink: 0;
  font-size: 0.8rem;
  color: #b45309;
}

.user-plan--managed-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.78rem;
  font-weight: 500;
}

.user-plan--managed-name {
  font-weight: 700;
  color: #78350f;
}

.loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;

  background-color: transparent;
  backdrop-filter: blur(2px);
  border-radius: 12px;
  overflow: hidden;
}

.loading-overlay::before {
  content: "";
  position: absolute;
  inset: 0;

  /* usa a cor do fundo geral, não a cor dos cards */
  background-color: var(--bg-primary, var(--bg-main, var(--bg-main)));

  opacity: 0.99;
  z-index: -1;
}

.loading-spinner {
  width: 2.25rem;
  height: 2.25rem;
  animation: spin 0.8s linear infinite;
  color: var(--brand);
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

.loading-text {
  font-size: 0.9rem;
  font-weight: 500;
  color: var(--text-muted);
}

.loading-fade-enter-active,
.loading-fade-leave-active {
  transition: opacity 0.25s ease;
}

.loading-fade-enter-from,
.loading-fade-leave-to {
  opacity: 0;
}

.account-label-row {
  display: flex;
  align-items: center;
}

/* Linha completa do plano */
.plan-row-full {
  grid-column: 1 / -1;
}

/* Título do plano + badge inline */
.plan-title-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: nowrap;
}

.plan-paused-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #c2410c;
  background-color: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 999px;
  padding: 0.1rem 0.55rem;
  line-height: 1;
}

.plan-paused-chip i,
.plan-paused-chip span {
  display: inline-flex;
  align-items: center;
  line-height: 1;
}

.plan-paused-chip i {
  font-size: 0.85em;
}

/* Botão Reativar */
.btn-chip-reactivate {
  background-color: var(--success-bg);
  color: #15803d;
  border: 1px solid #86efac;
}

.btn-chip-reactivate:hover:not(:disabled) {
  background-color: #bbf7d0;
}

.btn-spinner--green {
  border-color: #bbf7d0;
  border-top-color: #15803d;
}

/* Separador inline no card-subtext */
.card-subtext__sep {
  margin: 0 0.3rem;
  color: var(--neutral-400);
}

/* Plan card no formato stacked */
.plan-card--stacked {
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: start;
  gap: 0.75rem;
}

.plan-card--stacked .card-content {
  width: auto;
  min-width: 0;
  grid-column: 2;
}

.plan-card--stacked .card-value {
  margin-top: 0.05rem;
}

.plan-card--stacked .card-subtext {
  white-space: nowrap;
}

.plan-card--stacked .plan-actions--row {
  grid-column: 2;
  margin-left: 0;
  margin-top: 0rem;
  justify-content: flex-start;
  width: 100%;
}

.plan-card--stacked .card-icon {
  grid-column: 1;
  grid-row: 1 / span 2;
  align-self: center;
}

/* Botão Pausar renovação */
.btn-chip-pause {
  background-color: #fff7ed;
  color: #9a3412;
  border: 1px solid #fed7aa;
}

.btn-chip-pause:hover:not(:disabled) {
  background-color: #ffedd5;
}

.btn-spinner--orange {
  border-color: #fed7aa;
  border-top-color: #9a3412;
}

/* Crédito proporcional */
.credit-balance-chip {
  display: inline-flex;
  align-items: center;
  font-weight: 600;
  font-size: 0.78rem;
  color: #047857;
  white-space: nowrap;
}
</style>